import React, { useState,useCallback,  useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import FormatUrl from "../utils/UrlFormatter";
import { useTranslation } from "react-i18next";
import AddPractitioner from '../components/staffPortal/addPractitioner.component';
import AddAdminOwner from '../components/staffPortal/addAdminOwner.component';
import UpdatePractitioner from '../components/staffPortal/updatePractitioner';
import RemovePractitioner from '../components/staffPortal/removePractitioner';
import PractitionerList from '../components/staffPortal/practitionerList';
import StandardModal from '../components/Modals/standardModal';
import './staff.css';
import { getClinicId } from '../utils/config';

const Staff = ({logged_in, user, account}) => {
    const { t } = useTranslation();
 
    const clinicId = getClinicId(user);
    const navigate = useNavigate();
    const [showMessage, setShowMessage] = useState(false);

    useEffect(() => {
        if (!logged_in) {
            setShowMessage(true); 
            const timer = setTimeout(() => {
                navigate('/'); 
            }, 5000);

            return () => clearTimeout(timer); 
        }
    }, [logged_in, navigate]);
    
    const [loading, setLoading] = useState(false);
    const [shouldRefetch, setShouldRefetch] = useState(false);
    const [activeTab, setActiveTab] = useState('PractitionerList');
    const [practitioners, setPractitioners] = useState([]);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [modalTitle, setModalTitle] = useState('');
    const [modalMessage, setModalMessage] = useState('');
    const [showBanner, setShowBanner] = useState({ add: false, update: false, remove: false ,addAdmin: false});
    const [currentPage, setCurrentPage] = useState(1);
    const disciplinesPerPage = 2;
    const [disciplines, setDisciplines] = useState([]); 
    const [pracLimit, setPracLimit] = useState(null);


// Function to calculate total practitioners by discipline
    const getTotalByDiscipline = useCallback(() => {
        if (!Array.isArray(practitioners)) {
            return {};
        }

        const disciplineCounts = practitioners.reduce((counts, practitioner) => {
            counts[practitioner.discipline] = (counts[practitioner.discipline] || 0) + 1;
            return counts;
        }, {});

        return disciplineCounts;
    }, [practitioners]);

// Effect to update disciplines whenever practitioners or getTotalByDiscipline changes
    useEffect(() => {
        const updatedDisciplines = Object.keys(getTotalByDiscipline());
        setDisciplines(updatedDisciplines);
    }, [practitioners, getTotalByDiscipline]);
    
    // Pagination logic
    const indexOfLastDiscipline = currentPage * disciplinesPerPage;
    const indexOfFirstDiscipline = indexOfLastDiscipline - disciplinesPerPage;
    const currentDisciplines = disciplines.slice(indexOfFirstDiscipline, indexOfLastDiscipline);

    // Change page
    const paginate = (pageNumber) => setCurrentPage(pageNumber);
    

    // Handle Add, Update, Remove Practitioner
    const handleAddPractitioner = (newPractitioner) => {
        // Add the clinic_id to the practitioner data
        const practitionerWithClinicId = {
            ...newPractitioner,
            clinic_id: clinicId 
        };
    
        // Add the new practitioner with clinic_id to the list of practitioners
        const updatedPractitioners = [...practitioners, practitionerWithClinicId];
        
        // Update state and localStorage
        setPractitioners(updatedPractitioners);
    
        // Show success banner
        setShowBanner({ add: true, update: false, remove: false });
    
        // Switch to the PractitionerList tab after adding
        setActiveTab('PractitionerList');
    
        // Disable the Add tab to prevent accidental re-addition
        // setDisableAddTab(true);
    
        // Hide the banner after 3 seconds
        setTimeout(() => setShowBanner({ add: false, update: false, remove: false }), 3000);
    };
    
    const handleUpdatePractitioner = (updatedPractitioner) => {
        const updatedPractitioners = practitioners.map(p => 
            p.id === updatedPractitioner.id ? updatedPractitioner : p
        );
        setPractitioners(updatedPractitioners);
        setShowBanner({ add: false, update: true, remove: false });
        setActiveTab('PractitionerList');
        setTimeout(() => setShowBanner({ add: false, update: false, remove: false }), 3000);
    };

    
    const handleRemovePractitioner = (id) => {
        // Find the practitioner and mark them as inactive
        const updatedPractitioners = practitioners.map((practitioner) =>
            practitioner.id === id ? { ...practitioner, isActive: false } : practitioner
        );
    
        // Filter out inactive practitioners for display
        setPractitioners(updatedPractitioners.filter((p) => p.isActive));
        
        // Update local storage to maintain state after page reload
        
        // Show removal success banner
        setShowBanner({ add: false, update: false, remove: true });
        // Switch back to the Practitioner List tab
        setActiveTab('PractitionerList');
    
        // Reset banner after a delay
        setTimeout(() => setShowBanner({ add: false, update: false, remove: false }), 3000);
    };
    
    const handleAddAdminOwnerSuccess = () => {
        setShowBanner({ add: false, update: false, remove: false, addAdmin: true });
        setActiveTab('PractitionerList'); // Navigate back to the Practitioner List tab
        setTimeout(() => setShowBanner({ add: false, update: false, remove: false, addAdmin: false }), 3000);
      };

      
    // Close modal handler
    const closeModal = () => {
    setIsModalVisible(false); 
    setModalTitle('');  
    setModalMessage(''); 
    };
    
    // Add this function in the Staff component
    const handleAddSuccess = () => {
    setActiveTab('PractitionerList'); 
    // setDisableAddTab(true);   
    setShouldRefetch((prev) => !prev);     
    };

    const fetchPractitioners = useCallback(async () => {
        if (!clinicId) return;
    
        setLoading(true);
    
        try {
            const csrfUrl = FormatUrl(`/csrf-token/`);
            const csrfResponse = await fetch(csrfUrl, {
                method: 'GET',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
            });
            if (!csrfResponse.ok) throw new Error('Failed to fetch CSRF token');
    
            const csrfData = await csrfResponse.json();
            const csrfToken = csrfData.csrfToken;
    
            const apiUrl = FormatUrl(`/get-doctors/?clinic_id=${clinicId}`);
            const response = await fetch(apiUrl, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRFToken': csrfToken,
                },
                credentials: 'include',
            });
            if (!response.ok) throw new Error('Failed to fetch practitioners');
    
            const data = await response.json();
    
            setPractitioners(data.practitioners || []);
        } catch (error) {
            console.error('Error fetching practitioners:', error);
        } finally {
            setLoading(false);
        }
    }, [clinicId]);
    
    
      useEffect(() => {
        fetchPractitioners();
      }, [clinicId, fetchPractitioners,shouldRefetch]);
      
      const fetchClinicConfig = useCallback(async () => {
        if (!clinicId) return;
      
        try {
          const csrfUrl = FormatUrl(`/csrf-token/`);
          const csrfResponse = await fetch(csrfUrl, {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
          });
      
          if (!csrfResponse.ok) throw new Error('Failed to fetch CSRF token');
          const csrfData = await csrfResponse.json();
      
          const configUrl = FormatUrl(`/get-clinic-configuration/?clinic_id=${clinicId}`);
          const response = await fetch(configUrl, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              'X-CSRFToken': csrfData.csrfToken,
            },
            credentials: 'include',
          });
      
          if (!response.ok) throw new Error('Failed to fetch clinic configuration');
      
          const data = await response.json();

          if (data.status === "Success" && data.max_practitioners) {
            setPracLimit(data.max_practitioners);
          }
        } catch (error) {
          console.error("Error fetching prac_limit:", error);
        }
      }, [clinicId]);
      
      useEffect(() => {
        fetchClinicConfig();
      }, [fetchClinicConfig]);
      
          
    // Render Content based on activeTab only if search is performed
    const renderContent = () => {
        // Display loading indicator if loading is true
        if (loading) {
            return (
                <div className="loading-indicator">
                    <p>Loading data, please wait...</p>
                </div>
            );
        }
    
        // Continue with existing logic if not loading
        switch (activeTab) {
            case 'AddPractitioner':
                return (
                    <AddPractitioner
                        onAddPractitioner={handleAddPractitioner}
                        existingPractitioners={practitioners}
                        clinicId={clinicId} 
                        onSuccess={handleAddSuccess} 
                        pracLimit={pracLimit}
                    />
                );

            case 'PractitionerList':
                return <PractitionerList clinicId={clinicId} shouldRefetch={shouldRefetch} />;
            case 'UpdatePractitioner':
                return <UpdatePractitioner practitioners={practitioners} onUpdatePractitioner={handleUpdatePractitioner} />;
            case 'RemovePractitioner':
                return (
                    <RemovePractitioner 
                        practitioners={practitioners} 
                        onRemovePractitioner={handleRemovePractitioner} 
                        clinicId={clinicId} 
                    />
                );
            case 'Summary':
                // Check if there are any disciplines to display
                return (
                    <>
                        {/* Summary Section */}
                        <div className="custom-staff-summary">
                            {currentDisciplines.length === 0 ? (
                                <div className="custom-no-practitioner-card">
                                   <h4 className="custom-no-practitioner-title">No Practitioners Available for any of the disciplines</h4>
                                    <p className="custom-no-practitioner-description">Please add practitioners as per disciplines to see them summarized here.</p>
                                </div>
                            ) : (
                                currentDisciplines.map((discipline, index) => (
                                    <div className="custom-summary-card" key={index}>
                                        <h3 className='custom-summary-title'>{discipline}</h3>
                                        <p>{getTotalByDiscipline()[discipline]} Practitioners</p>
                                    </div>
                                ))
                            )}
                        </div>
    
                        {/* Pagination Section */}
                        {currentDisciplines.length > 0 && (
                            <div className="custom-pagination-container">
                                <Pagination
                                    disciplinesPerPage={disciplinesPerPage}
                                    totalDisciplines={disciplines.length}
                                    paginate={paginate}
                                    currentPage={currentPage}
                                />
                            </div>
                        )}
                    </>
                );
                case 'AddAdminOwner':
                return (
                    <AddAdminOwner 
                    clinicId={clinicId}
                    // temporary no-op handler - need to change once backend is ready
                    onSuccess={handleAddAdminOwnerSuccess} 
                    />
                );

            default:
                return <PractitionerList clinicId={clinicId} shouldRefetch={shouldRefetch} />;
        }
    };
    
    return (
        <>
        {/* Check if user is logged in */}
        {!logged_in && showMessage ? (
            <div className="landing-page-container">
                <div className="logout-message-container">
                    <h2 className="logout-message-title">Please log in to access the Staff Management Portal.</h2>
                </div>
            </div>
        ) : (
            <>
                {/* Check if the user is Clinic Admin */}
                {!(user.role === 'CO' || user.role === 'CA') ? (
                    <div className="landing-page-container">
                    <div className="logout-message-container">
                      <h2 className="logout-message-title">{t("staff_main.access_restricted")}</h2>
                        <p>{t("staff_main.no_permission")}</p>
                      </div>
                  </div>
                ) : (
                    <div className="staff-container">
                        {/* Header Section */}
                        <div className="staff-header">
                            <h1>{t("staff_main.staff_management_portal")}</h1>
                            <p>{t("staff_main.manage_practitioners")}</p>
                        </div>
                        <div className="mini-workflow-container">
                            <div className="workflow-description">
                                <p><span className="workflow-icon">👉</span> <strong>{t("staff_main.practitioner_list")}:</strong> {t("staff_main.practitioner_list_desc")}</p>
                                <p><span className="workflow-icon">👉</span> <strong>{t("staff_main.add_practitioner")}:</strong> {t("staff_main.add_practitioner_desc")}</p>
                                <p><span className="workflow-icon">👉</span> <strong>{t("staff_main.deactivate_practitioner")}:</strong> {t("staff_main.deactivate_practitioner_desc")}</p>
                                <p><span className="workflow-icon">👉</span> <strong>{t("staff_main.add_admin_owner")}:</strong> {t("staff_main.add_admin_owner_desc")}</p>
                                <p><span className="workflow-icon">👉</span> <strong>{t("staff_main.summary")}:</strong> {t("staff_main.summary_desc")}</p>
                            </div>
                        </div>
                        {pracLimit && (
                            <p className="limit-hint">
                            {t("staff_main.clinic_practitioner_limit", { count: pracLimit })} — {t("staff_main.modify_hint")}
                          </p>
                          
                          
                        )}

                        <hr className="section-divider" />

                        <h2 className='custom-manage-title'>{t("staff_main.manage_practitioners_button")}</h2>
                        {showBanner.add && (
                            <div className="banner">
                                {t("staff_main.successfully_added")}
                            </div>
                        )}
                        {showBanner.update && (
                            <div className="banner">
                                {t("staff_main.successfully_updated")}
                            </div>
                        )}
                        {showBanner.remove && (
                            <div className="banner">
                                {t("staff_main.successfully_removed")}
                            </div>
                        )}

                        <div className="staff-tabs">
                            <button
                                className={`staff-tab-btn ${activeTab === 'PractitionerList' ? 'active-tab' : ''}`}
                                onClick={() => setActiveTab('PractitionerList')}
                            >
                                {t("staff_main.practitioner_list")}
                            </button>
    
                            <button
                                className={`staff-tab-btn ${activeTab === 'AddPractitioner' ? 'active-tab' : ''}`}
                                onClick={() => setActiveTab('AddPractitioner')}
                            >
                                {t("staff_main.add_practitioner")}
                            </button>


                            {/* Disabling Update Practitioner for time-being */}
                            {/* <button
                                className={`staff-tab-btn ${activeTab === 'UpdatePractitioner' ? 'active-tab' : ''}`}
                                onClick={() => setActiveTab('UpdatePractitioner')}
                                // Keep Update enabled even before search
                            >
                                Update Practitioner
                            </button> */}
                            
                            <button
                                className={`staff-tab-btn ${activeTab === 'RemovePractitioner' ? 'active-tab' : ''}`}
                                onClick={() => setActiveTab('RemovePractitioner')}
                                // Keep Remove enabled even before search
                            >
                                {t("staff_main.remove_practitioner")}
                            </button>
                            <button
                                className={`staff-tab-btn ${activeTab === 'AddAdminOwner' ? 'active-tab' : ''}`}
                                onClick={() => setActiveTab('AddAdminOwner')}
                                >
                                {t("staff_main.add_admin_owner")}
                            </button>
                            <button
                                className={`staff-tab-btn ${activeTab === 'Summary' ? 'active-tab' : ''}`}
                                onClick={() => setActiveTab('Summary')}
                            >
                                {t("staff_main.summary")}
                            </button>
                        </div>


                        <div className="staff-content">
                            {renderContent()}
                        </div>

                        
                {/* Modal Usage */}
                <StandardModal
                isOpen={isModalVisible}
                onClose={closeModal}
                title={modalTitle}
                message={modalMessage}
                />
                    </div>
                )}
            </>
        )}
    </>
  );
}

// Pagination Component
const Pagination = ({ disciplinesPerPage, totalDisciplines, paginate, currentPage }) => {
    const pageNumbers = [];
    for (let i = 1; i <= Math.ceil(totalDisciplines / disciplinesPerPage); i++) {
        pageNumbers.push(i);
    }

    return (
        <nav className="pagination-nav">
            <ul className="pagination">
                {pageNumbers.map((number) => (
                    <li key={number} className={`page-item ${currentPage === number ? 'active' : ''}`}>
                        <button onClick={() => paginate(number)} className="page-link">
                            {number}
                        </button>
                    </li>
                ))}
            </ul>
        </nav>
    );
};


export default Staff;
